import React, { useState } from "react";
import { MapPin, Phone, Mail, Clock, Building2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { contactInfo } from "../data/mock";
import { toast } from "sonner";

export const Contact = () => {
  const [formData, setFormData] = useState({
    firma: "",
    name: "",
    email: "",
    telefon: "",
    beschreibung: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Build mailto link with form data
    const subject = `Anfrage von ${formData.firma} - Gerüstbeläge`;
    const body = `
Neue Anfrage über das Kontaktformular:

FIRMA: ${formData.firma}
ANSPRECHPERSON: ${formData.name}
E-MAIL: ${formData.email}
TELEFON: ${formData.telefon || "Nicht angegeben"}

BESCHREIBUNG / ANZAHL BELÄGE:
${formData.beschreibung}
    `.trim();

    const mailtoLink = `mailto:${contactInfo.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    
    // Open email client
    window.location.href = mailtoLink;
    
    toast.success("Ihr E-Mail-Programm wird geöffnet...");
  };

  const contactCards = [
    {
      icon: Building2,
      title: "Postanschrift",
      lines: [contactInfo.postAddress.street, contactInfo.postAddress.city],
    },
    {
      icon: MapPin,
      title: "Werkstatt",
      lines: [contactInfo.workshop.street, contactInfo.workshop.city, contactInfo.workshop.access],
    },
    {
      icon: Phone,
      title: "Telefon",
      lines: [contactInfo.phone, `Mobil: ${contactInfo.mobile}`],
      isClickable: true,
      href: `tel:${contactInfo.phoneShort.replace(/\s/g, "")}`,
    },
    {
      icon: Mail,
      title: "E-Mail",
      lines: [contactInfo.email],
      isClickable: true,
      href: `mailto:${contactInfo.email}`,
    },
    {
      icon: Clock,
      title: "Servicezeiten",
      lines: [contactInfo.serviceHours],
    },
  ];

  return (
    <section id="kontakt" className="py-24 bg-neutral-light">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-accent font-semibold text-sm tracking-wider uppercase mb-4">
            Kontakt aufnehmen
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-primary mb-4">
            Kontakt
          </h2>
          <p className="text-lg text-secondary max-w-2xl mx-auto">
            Haben Sie Gerüstbeläge zur Aufbereitung? Kontaktieren Sie uns für eine unverbindliche Anfrage.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left - Contact Info */}
          <div>
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {contactCards.map((card, index) => {
                const IconComponent = card.icon;
                const CardWrapper = card.isClickable ? "a" : "div";
                return (
                  <CardWrapper
                    key={index}
                    href={card.href}
                    className={`bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow ${
                      card.isClickable ? "cursor-pointer" : ""
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <IconComponent className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-primary text-sm mb-1">
                          {card.title}
                        </h4>
                        {card.lines.map((line, i) => (
                          <p key={i} className="text-secondary text-sm">
                            {line}
                          </p>
                        ))}
                      </div>
                    </div>
                  </CardWrapper>
                );
              })}
            </div>

            {/* Map / Image Area */}
            <div className="bg-white rounded-xl overflow-hidden shadow-sm">
              <div className="aspect-video relative">
                <img 
                  src="/images/lagerhalle.jpg"
                  alt="Lagerhalle"
                  className="absolute inset-0 w-full h-full object-cover grayscale"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <div className="flex items-center gap-3">
                    <MapPin className="w-6 h-6 text-accent" />
                    <div>
                      <p className="font-semibold">Werkstatt Balzheim</p>
                      <p className="text-sm opacity-90">Sterngasse 5, 88481 Balzheim</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Contact Form */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h3 className="text-xl font-bold text-primary mb-6">
              Unverbindlich anfragen
            </h3>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firma" className="text-primary font-medium">
                    Firma *
                  </Label>
                  <Input
                    id="firma"
                    name="firma"
                    value={formData.firma}
                    onChange={handleChange}
                    required
                    className="mt-1.5"
                    placeholder="Ihre Firma"
                  />
                </div>
                <div>
                  <Label htmlFor="name" className="text-primary font-medium">
                    Ansprechperson *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="mt-1.5"
                    placeholder="Ihr Name"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email" className="text-primary font-medium">
                      E-Mail *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="mt-1.5"
                      placeholder="ihre@email.de"
                    />
                  </div>
                  <div>
                    <Label htmlFor="telefon" className="text-primary font-medium">
                      Telefon
                    </Label>
                    <Input
                      id="telefon"
                      name="telefon"
                      type="tel"
                      value={formData.telefon}
                      onChange={handleChange}
                      className="mt-1.5"
                      placeholder="Optional"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="beschreibung" className="text-primary font-medium">
                    Kurzbeschreibung / Anzahl Beläge *
                  </Label>
                  <Textarea
                    id="beschreibung"
                    name="beschreibung"
                    value={formData.beschreibung}
                    onChange={handleChange}
                    required
                    className="mt-1.5 min-h-[120px]"
                    placeholder="Beschreiben Sie kurz Ihre Gerüstbeläge und den gewünschten Service..."
                  />
                </div>

                <div className="pt-2">
                  <Button
                    type="submit"
                    className="w-full bg-accent hover:bg-accent/90 text-neutral-dark font-semibold py-6 text-base"
                  >
                    <span className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      E-Mail senden
                    </span>
                  </Button>
                </div>

                <p className="text-xs text-secondary text-center">
                  Öffnet Ihr E-Mail-Programm mit den eingegebenen Daten.
                </p>
              </form>
          </div>
        </div>
      </div>
    </section>
  );
};
